import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Share2, Check } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface ShareProfileButtonProps {
  userId: string;
  username: string;
  variant?: "default" | "outline" | "ghost";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
}

export function ShareProfileButton({
  userId,
  username,
  variant = "outline",
  size = "sm",
  className
}: ShareProfileButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    const profileUrl = `${window.location.origin}/users/${userId}`;
    
    try {
      // Try native share API first (mobile)
      if (navigator.share) {
        await navigator.share({
          title: `${username}'s Profile on ThePromptSpace`,
          text: `Check out ${username}'s AI prompts on ThePromptSpace`,
          url: profileUrl,
        });
        return;
      }
      
      // Fallback to clipboard
      await navigator.clipboard.writeText(profileUrl);
      setCopied(true);
      toast.success("Profile link copied to clipboard!");
      
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      // User cancelled share or error occurred
      if ((error as Error).name !== "AbortError") {
        console.error("Share failed:", error);
        toast.error("Failed to share profile");
      }
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleShare}
      className={cn(
        "gap-2 transition-all duration-200",
        copied && "text-green-400 border-green-400/50",
        className
      )}
    >
      {copied ? (
        <>
          <Check className="w-4 h-4" />
          <span className="hidden sm:inline">Copied!</span>
        </>
      ) : (
        <>
          <Share2 className="w-4 h-4" />
          <span className="hidden sm:inline">Share</span>
        </>
      )}
    </Button>
  );
}
